// src/pages/Techniques.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, slideInLeft, slideInRight, scaleIn } from '../styles/animations';
import SectionTitle from '../components/ui/SectionTitle';

const TechniquesPageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const HeroSection = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.light};
  text-align: center;
`;

const HeroContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  color: ${props => props.theme.colors.light};
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.bgColor || props.theme.colors.light};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const TechniqueTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const TechniqueContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const TextContent = styled.div`
  animation: ${slideInLeft} 0.8s ease-out forwards;
  opacity: 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: 1.7;
  }
  
  ul {
    margin-bottom: ${props => props.theme.spacing.md};
    padding-left: 1.5rem;
    
    li {
      margin-bottom: ${props => props.theme.spacing.xs};
    }
  }
`;

const ImageContent = styled.div`
  animation: ${slideInRight} 0.8s ease-out forwards;
  opacity: 0;
`;

const MainImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
`;

const GalleryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.small};
  box-shadow: ${props => props.theme.shadows.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease;
  
  ${props => props.isOpen && `
    opacity: 1;
    visibility: visible;
  `}
`;

const ModalContent = styled.div`
  max-width: 90%;
  max-height: 90%;
  position: relative;
  
  img {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${props => props.theme.borderRadius.medium};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
`;

const Techniques = () => {
    const [selectedImage, setSelectedImage] = useState(null);

    const openModal = (imageSrc) => {
        setSelectedImage(imageSrc);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    const finitionsImages = [
        "https://images.unsplash.com/photo-1595423573597-527ed96d9c49?q=80&w=500",
        "https://images.unsplash.com/photo-1589834390005-5d4fb9bf3d32?q=80&w=500",
        "https://images.unsplash.com/photo-1595828071663-5e84e3025dcf?q=80&w=500",
        "https://images.unsplash.com/photo-1614357235247-99fabbee67f9?q=80&w=500",
    ];

    const fixationsImages = [
        "https://images.unsplash.com/photo-1591364469468-6a1ddf320a85?q=80&w=500",
        "https://images.unsplash.com/photo-1615529328331-f8917597711f?q=80&w=500",
        "https://images.unsplash.com/photo-1613685703305-8bbec788e736?q=80&w=500",
        "https://images.unsplash.com/photo-1614430715308-1966f9615db7?q=80&w=500",
    ];

    return (
        <TechniquesPageWrapper>
            <Helmet>
                <title>Nos Services - DeckO | Terrasses bois, composite et céramique à Nantes</title>
                <meta name="description" content="Conception et pose de terrasses bois et composite, dallage, escaliers, garde-corps, rénovation et aménagement sur mesure. Devis gratuit." />
            </Helmet>

            <HeroSection>
                <HeroContainer>
                    <HeroTitle>Nos Services</HeroTitle>
                    <HeroSubtitle>Savoir-faire et excellence pour des réalisations durables</HeroSubtitle>
                </HeroContainer>
            </HeroSection>

            <Section>
                <SectionContainer>
                    <TechniqueTitle>Les prestations</TechniqueTitle>

                    <TechniqueContent>
                        <TextContent>
                            <ul>
                                <li>Conception et pose de terrasses bois et composite</li>
                                <li>Dallage, caillebotis et lames sur plots</li>
                                <li>Escaliers extérieurs et garde-corps contemporains</li>
                                <li>Entretien, traitement et rénovation de terrasses</li>
                                <li>Aménagement sur mesure (protection solaire, éclairages, bacs à végétaux, occultations)</li>
                            </ul>
                            <p>
                                Chez DeckO, nous accordons une importance particulière aux finitions, car ce sont ces détails qui font toute la différence dans la qualité d'une réalisation.
                            </p>
                            <p>
                                <strong>Variantes chantiers participatifs</strong> selon votre envie de vous impliquer et votre budget :
                            </p>
                            <ul>
                                <li>Fourniture seule des matériaux + conseils de démarrage sur place + accompagnement visio</li>
                                <li>Montage par nos soins du lambourdage ou de la charpente de la terrasse, puis vous posez les matériaux fournis</li>
                            </ul>
                        </TextContent>

                        <ImageContent>
                            <MainImage
                                src="/20230925_125657.webp"
                                alt="Services DeckO"
                            />
                        </ImageContent>
                    </TechniqueContent>
                </SectionContainer>
            </Section>

            <Section bgColor={props => props.theme.colors.gray}>
                <SectionContainer>
                    <TechniqueTitle>Pourquoi choisir DeckO ?</TechniqueTitle>

                    <TechniqueContent>
                        <ImageContent>
                            <MainImage
                                src="/terBalcon_foto1.webp"
                                alt="Réalisations DeckO"
                            />
                        </ImageContent>

                        <TextContent>
                            <ul>
                                <li>Nos réalisations depuis 2004 et avis clients vérifiés Google, Houzz, Pages Jaunes — <strong>5/5</strong></li>
                                <li>Entreprise locale et pignon sur rue : atelier, showroom et stock à La Haie-Fouassière</li>
                                <li>Devis clair et gratuit + plan 2D/3D selon projet</li>
                                <li>Intervention sur l'agglo de Nantes et le littoral, respect des normes</li>
                            </ul>
                            <p>
                                <strong>Besoin d'un devis rapide ?</strong> Demandez votre visite gratuite — envoyez-nous des photos, croquis ou réservez un créneau au <strong>06 83 58 99 05</strong>
                            </p>
                        </TextContent>
                    </TechniqueContent>
                </SectionContainer>
            </Section>

            <Modal isOpen={selectedImage !== null} onClick={closeModal}>
                <ModalContent onClick={e => e.stopPropagation()}>
                    <CloseButton onClick={closeModal}>&times;</CloseButton>
                    {selectedImage && <img src={selectedImage} alt="Agrandissement" />}
                </ModalContent>
            </Modal>

        </TechniquesPageWrapper>
    );
};

export default Techniques;